import { useCallback, useMemo } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Field,
  Form,
  FormSizes,
  Modal,
  ModalSizes,
  TextInput,
  Typography,
  TypographyColors,
  TypographyVariants,
  maxLength,
} from '@carboledger/ui';
import { GeneratedIdField, TextField } from '@/components/common';
import { useTranslations } from '@/hooks';
import { useCreateSupplierMutation, useUpdateSupplierMutation } from '../api/suppliersApi';
import {
  EMPTY_SUPPLIER_FORM,
  MAX_SHIPPING_SITES,
  ORGANIZATION_NAME_MAX_LENGTH,
  SHIPPING_SITE_KEYS_FIELD,
  SUPPLIER_FIELDS,
  emptyShippingSiteValues,
  nextShippingSiteKey,
} from '../constants/suppliers.constants';
import { ShippingSiteFields } from './ShippingSiteFields';
import styles from './SupplierModal.module.scss';

const OrganizationNameField = ({ disabled }) => {
  const t = useTranslations();
  const rules = useMemo(() => [maxLength(ORGANIZATION_NAME_MAX_LENGTH)], []);

  return (
    <Field name={SUPPLIER_FIELDS.organizationName} required rules={rules}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <div className={styles.field}>
          <TextInput
            id={id}
            name={name}
            label={t('FORM_ORGANIZATION_NAME')}
            placeholder={t('FORM_ORGANIZATION_NAME_PLACEHOLDER')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
            required
            disabled={disabled}
          />
          <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
            {t('FORM_CHARACTER_COUNT', { count: value.length, max: ORGANIZATION_NAME_MAX_LENGTH })}
          </Typography>
        </div>
      )}
    </Field>
  );
};

const ShippingSitesSection = ({ siteKeys, setValue, disabled }) => {
  const t = useTranslations();

  const handleAdd = useCallback(() => {
    const siteKey = nextShippingSiteKey(siteKeys);
    setValue(SHIPPING_SITE_KEYS_FIELD, [...siteKeys, siteKey]);
    Object.entries(emptyShippingSiteValues(siteKey)).forEach(([name, value]) =>
      setValue(name, value),
    );
  }, [siteKeys, setValue]);

  // Only the key list is dropped; the card's own values fall out of the payload with it.
  const handleRemove = useCallback(
    (siteKey) =>
      setValue(
        SHIPPING_SITE_KEYS_FIELD,
        siteKeys.filter((key) => key !== siteKey),
      ),
    [siteKeys, setValue],
  );

  return (
    <>
      <div className={styles.shippingHeader}>
        <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
          {t('SECTION_SHIPPING_DETAILS_OPTIONAL')}
        </Typography>
        <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
          {t('SUPPLIERS_SHIPPING_SECTION_DESCRIPTION')}
        </Typography>
      </div>

      {siteKeys.map((siteKey, index) => (
        <ShippingSiteFields
          key={siteKey}
          siteKey={siteKey}
          position={index + 1}
          onRemove={handleRemove}
          canRemove={siteKeys.length > 1}
          disabled={disabled}
        />
      ))}

      <Button
        variant={ButtonVariants.secondary}
        size={ButtonSizes.sm}
        fullWidth
        onClick={handleAdd}
        disabled={disabled || siteKeys.length >= MAX_SHIPPING_SITES}
      >
        {t('SHIPPING_SITE_ADD_MORE')}
      </Button>
    </>
  );
};

const SupplierFields = ({ values, setValue, disabled }) => {
  const t = useTranslations();
  const siteKeys = values[SHIPPING_SITE_KEYS_FIELD];

  return (
    <div className={styles.fields}>
      <div className={styles.sectionHeader}>
        <Typography
          variant={TypographyVariants.caption}
          color={TypographyColors.secondary}
          className={styles.sectionLabel}
        >
          {t('SUPPLIERS_SECTION_DETAILS')}
        </Typography>
        <div className={styles.sectionLine} />
      </div>

      <OrganizationNameField disabled={disabled} />
      <GeneratedIdField
        name={SUPPLIER_FIELDS.supplierId}
        label={t('FIELD_SUPPLIER_ID')}
        placeholder={t('FORM_SUPPLIER_ID_PLACEHOLDER')}
      />

      <Form.Row columns={2}>
        <TextField
          name={SUPPLIER_FIELDS.primaryEmail}
          label={t('FORM_PRIMARY_EMAIL_OPTIONAL')}
          placeholder={t('FORM_PRIMARY_EMAIL_PLACEHOLDER')}
          disabled={disabled}
        />
        <TextField
          name={SUPPLIER_FIELDS.primaryContactName}
          label={t('FORM_PRIMARY_CONTACT_NAME_OPTIONAL')}
          placeholder={t('FORM_PRIMARY_CONTACT_NAME_PLACEHOLDER')}
          disabled={disabled}
        />
      </Form.Row>

      <ShippingSitesSection siteKeys={siteKeys} setValue={setValue} disabled={disabled} />
    </div>
  );
};

const hasRequiredFields = (values) =>
  (values[SUPPLIER_FIELDS.organizationName] ?? '').trim() !== '' &&
  (values[SUPPLIER_FIELDS.supplierId] ?? '').trim() !== '';

/**
 * Props:
 *   open      whether the modal is shown
 *   onClose   close handler (✕ / Cancel)
 *   supplier  the supplier being edited as { id, values }; omit to add a new one
 */
export const SupplierModal = ({ open, onClose, supplier }) => {
  const t = useTranslations();
  const [createSupplier, { isLoading: isCreating }] = useCreateSupplierMutation();
  const [updateSupplier, { isLoading: isUpdating }] = useUpdateSupplierMutation();

  const isEdit = Boolean(supplier);
  const saving = isCreating || isUpdating;

  const initialValues = useMemo(
    () => ({ ...EMPTY_SUPPLIER_FORM, ...(supplier?.values ?? {}) }),
    [supplier?.values],
  );

  const handleSubmit = useCallback(
    async (values) => {
      const result = isEdit
        ? await updateSupplier({ id: supplier.id, values })
        : await createSupplier(values);

      if (result.error) return;
      onClose();
    },
    [isEdit, supplier?.id, createSupplier, updateSupplier, onClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ values, setValue, submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={isEdit ? t('SUPPLIERS_EDIT_TITLE') : t('SUPPLIERS_ADD')}
          onClose={onClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
            {
              label: t('COMMON_SAVE'),
              variant: ButtonVariants.primary,
              disabled: saving || !hasRequiredFields(values),
              onClick: submit,
            },
          ]}
        >
          <SupplierFields values={values} setValue={setValue} disabled={saving} />
        </Modal>
      )}
    </Form>
  );
};
